import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppSettings } from '../../utils/app-settings';
import { ICreateRole, IGetRoleById, IGetRoles, IGetRolesPaginated, IRole, IUpdateRole } from '../../interfaces/roles.interface';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class RolesService {
  constructor(private http: HttpClient, private appSettings: AppSettings) {}

    getRoles(page: number = 1, pageSize: number = 10, q?: string): Observable<IGetRolesPaginated> {
      let url = `${this.appSettings.roles.urls.getRoles}?page=${page}&pageSize=${pageSize}`;
      if (q) url += `&q=${encodeURIComponent(q)}`;
      return this.http.get<IGetRolesPaginated>(url);
    }

    getRolesSuperAdmin(page: number = 1, pageSize: number = 10): Observable<IGetRolesPaginated> {
      const url = `${this.appSettings.roles.urls.getRolesSuperAdmin}?page=${page}&pageSize=${pageSize}`;
      return this.http.get<IGetRolesPaginated>(url);
    }

    getRolesForUsers(page: number = 1, pageSize: number = 200): Observable<IGetRolesPaginated> {
      const url = `${this.appSettings.roles.urls.getRolesForUsers}?page=${page}&pageSize=${pageSize}`;
      return this.http.get<IGetRolesPaginated>(url);
    }

    getRoleById(id: number): Observable<IGetRoleById> {
      const url = `${this.appSettings.roles.urls.getRoleById}/${id}`;
      return this.http.get<IGetRoleById>(url);
    }

    createRole(role: IRole): Observable<ICreateRole> {
      const url = this.appSettings.roles.urls.createRole;
      return this.http.post<ICreateRole>(url, role);
    }

    updateRole(id: number, role: IRole): Observable<IUpdateRole> {
      const url = `${this.appSettings.roles.urls.updateRole}/${id}`;
      return this.http.patch<IUpdateRole>(url, role);
    }

    deleteRole(id: number): Observable<any> {
      const url = `${this.appSettings.roles.urls.deleteRole}/${id}`;
      return this.http.delete<any>(url);
    }

    private allPermissions: string[] = [
      'users_create',
      'users_read',
      'users_update',
      'devoluciones_upload_file',
      'envios_upload_file',
      'users_delete',
      'envios_read',
      'roles_read',
      'roles_create',
      'roles_update',
      'roles_delete',
      'devoluciones_read',
      'perfil_read',
      'perfil_update',
      'package_read',
      'package_update',
      'dashboard_read',
      'web_envios_read',
      'web_envios_upload_file',
      'web_devoluciones_read',
      'web_devoluciones_upload_file',
      'ac_inicio_read',
      'ac_tablero_read',
      'ac_tablero_update',
      'ac_tablero_import',
      'ac_tablero_messages',
      'ac_tablero_delete',
      'ac_ocr_read',
      'ac_ocr_upload',
      'ac_ocr_download',
      'ac_ocr_validate',
      'ac_ocr_delete',
      'ac_ocr_flag',
      'motivos_no_entrega_manage',
      'conductores_unlink',
    ];

     usersPermissions: string[] = [
      'users_read',
      'users_update',
      'users_create',
      'users_delete'
    ];

     rolesPermissions: string[] = [
      'roles_read',
      'roles_create',
      'roles_update',
      'roles_delete'
    ];

    enviosPermissions: string[] = [
      'envios_read',
      'envios_upload_file'
    ];

    devolucionesPermissions: string[] = [
      'devoluciones_read',
      'devoluciones_upload_file'
    ];

    webEnviosPermissions: string[] = [
      'web_envios_read',
      'web_envios_upload_file'
    ];

    webDevolucionesPermissions: string[] = [
      'web_devoluciones_read',
      'web_devoluciones_upload_file'
    ];

    configuracionPermissions: string[] = [
      'motivos_no_entrega_manage'
    ];

    conductoresPermissions: string[] = [
      'conductores_unlink'
    ];

    perfilPermissions: string[] = [
      'perfil_read',
      'perfil_update'
    ];

    packagePermissions: string[] = [
      'package_read',
      'package_update'
    ];

    dashboardPermissions: string[] = [
      'dashboard_read'
    ]

    atencionClienteInicioPermissions: string[] = [
      'ac_inicio_read',
    ];

    atencionClienteTableroPermissions: string[] = [
      'ac_tablero_read',
      'ac_tablero_update',
      'ac_tablero_import',
      'ac_tablero_messages',
      'ac_tablero_delete',
    ];

    atencionClienteOcrPermissions: string[] = [
      'ac_ocr_read',
      'ac_ocr_upload',
      'ac_ocr_download',
      'ac_ocr_validate',
      'ac_ocr_delete',
      'ac_ocr_flag',
    ];

    private permissionLabels: { [key: string]: string } = {
      users_create: 'Crear usuarios',
      users_read: 'Leer usuarios',
      users_update: 'Actualizar usuarios',
      devoluciones_upload_file: 'Subir archivo de devoluciones',
      envios_upload_file: 'Subir archivo de envíos',
      users_delete: 'Eliminar usuarios',
      envios_read: 'Leer envíos',
      roles_read: 'Leer roles',
      roles_create: 'Crear roles',
      roles_update: 'Actualizar roles',
      roles_delete: 'Eliminar roles',
      devoluciones_read: 'Leer devoluciones',
      perfil_read: 'Leer perfil',
      perfil_update: 'Actualizar perfil',
      package_read: 'Leer paquetes',
      package_update: 'Actualizar paquetes',
      dashboard_read: 'Leer dashboard',
      ac_inicio_read: 'Ver',
      ac_tablero_read: 'Ver',
      ac_tablero_update: 'Editar',
      ac_tablero_import: 'Importar pedidos',
      ac_tablero_messages: 'Enviar mensajes',
      ac_tablero_delete: 'Eliminar',
      ac_ocr_read: 'Ver',
      ac_ocr_upload: 'Importar PDFs',
      ac_ocr_download: 'Exportar',
      ac_ocr_validate: 'Validar',
      ac_ocr_delete: 'Eliminar',
      ac_ocr_flag: 'Enviar a validar',
      motivos_no_entrega_manage: 'Gestionar motivos de no entrega',
      conductores_unlink: 'Desvincular usuario de conductor',
    };

    platformPermissions: { [platform: string]: { [module: string]: string[] } } = {
      // [PASAJE-PROD] App de transportistas (Logística) deshabilitada: el grupo no se muestra en el selector de permisos de Roles
      // app: {
      //   envios: this.enviosPermissions,
      //   devoluciones: this.devolucionesPermissions,
      // },
      web_atencion_cliente: {
        inicio: this.atencionClienteInicioPermissions,
        tablero: this.atencionClienteTableroPermissions,
        ocr: this.atencionClienteOcrPermissions,
      },
      web_general: {
        users: this.usersPermissions,
        roles: this.rolesPermissions,
      },
      // [PASAJE-PROD] Logística deshabilitada: el grupo no se muestra en el selector de permisos de Roles
      // web_logistica: {
      //   dashboard: this.dashboardPermissions,
      //   envios: this.webEnviosPermissions,
      //   devoluciones: this.webDevolucionesPermissions,
      //   motivosNoEntrega: this.configuracionPermissions,
      //   conductores: this.conductoresPermissions,
      // },
    };

    modulesPermissions: { [key: string]: string[] } = {
      users: this.usersPermissions,
      roles: this.rolesPermissions,
      envios: this.enviosPermissions,
      devoluciones: this.devolucionesPermissions,
      perfil: this.perfilPermissions,
      package: this.packagePermissions,
      dashboard: this.dashboardPermissions,
      motivosNoEntrega: this.configuracionPermissions,
      conductores: this.conductoresPermissions
    }

    permissionModulesLabels: { [platform: string]: { [module: string]: { [permission: string]: string } } } = {
      app: {
        envios: {
          envios_read: 'Ver y Actualizar',
          envios_upload_file: 'Subir archivos'
        },
        devoluciones: {
          devoluciones_read: 'Ver y Actualizar',
          devoluciones_upload_file: 'Subir archivo'
        },
      },
      web_atencion_cliente: {
        inicio: {
          ac_inicio_read: 'Ver',
        },
        tablero: {
          ac_tablero_read: 'Ver',
          ac_tablero_update: 'Editar',
          ac_tablero_import: 'Importar pedidos',
          ac_tablero_messages: 'Enviar mensajes',
          ac_tablero_delete: 'Eliminar',
        },
        ocr: {
          ac_ocr_read: 'Ver',
          ac_ocr_upload: 'Importar PDFs',
          ac_ocr_download: 'Exportar',
          ac_ocr_validate: 'Validar',
          ac_ocr_delete: 'Eliminar',
          ac_ocr_flag: 'Enviar a validar',
        },
      },
      web_general: {
        roles: {
          roles_read: 'Ver',
          roles_create: 'Crear',
          roles_update: 'Actualizar',
          roles_delete: 'Eliminar'
        },
        users: {
          users_create: 'Crear',
          users_read: 'Ver',
          users_update: 'Actualizar',
          users_delete: 'Eliminar'
        },
      },
      web_logistica: {
        dashboard: {
          dashboard_read: 'Ver'
        },
        envios: {
          web_envios_read: 'Ver',
          web_envios_upload_file: 'Subir archivos'
        },
        devoluciones: {
          web_devoluciones_read: 'Ver',
          web_devoluciones_upload_file: 'Subir archivo'
        },
        motivosNoEntrega: {
          motivos_no_entrega_manage: 'Gestionar',
        },
        conductores: {
          conductores_unlink: 'Desvincular usuario',
        },
      },
    }


    getPermissionLabels() {
      return this.permissionLabels;
    }

    getSuperAdminPermissions() {
      return this.allPermissions;
    }

    getAdminPermissions() {
      return this.allPermissions.filter(permission => permission !== 'superadmin_create');
    }

    getPermissionModulesLabels() {
      return this.permissionModulesLabels;
    }

    getModulesPermissions() {
      return this.modulesPermissions;
    }

    getPlatformPermissions() {
      return this.platformPermissions;
    }
}
